import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import KeyboardConnectMidi from 'assets/images/KeyboardConnectMidi.png';
import { OFFLINE_BACKDROP_Z_INDEX, OFFLINE_MESSAGE_Z_INDEX } from 'Constants/StackingLevels';
import { useBlockingGateHoldsBack } from 'Hooks/useHardwareBack';

// Fetches a picture once, while the app is online, and returns an in-memory
// address for it (the original address until the fetch is done, or if it fails).
const usePictureKeptInMemory = (pictureUrl: string): string => {
  const [memoryUrl, setMemoryUrl] = useState<string | null>(null);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    fetch(pictureUrl)
      .then((response) => (response.ok ? response.blob() : Promise.reject(response.status)))
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setMemoryUrl(objectUrl);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [pictureUrl]);

  return memoryUrl ?? pictureUrl;
};

// Full-screen blocking modal shown when the app is offline.
// Does not rely on external CSS; fully styled here.
const OfflineModal: React.FC = () => {
  const [online, setOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );
  // MUS-607: the picture was first asked for when the modal opened, which is
  // when there is no network, so the browser drew a broken-image icon over the
  // message. Fetch it while the app is still online and show it from memory;
  // leave it out if it cannot be loaded.
  const pictureSrc = usePictureKeptInMemory(KeyboardConnectMidi);
  const [pictureFailed, setPictureFailed] = useState(false);

  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Android's Back cannot leave this screen for the one beneath it: it sends the app to the
  // background, and the lesson, a dialog or the keyboard prompt beneath stay as they are.
  useBlockingGateHoldsBack(!online);

  if (online) return null;

  return (
    <>
      {/* Backdrop overlay */}
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0, 0, 0, 0.6)',
          zIndex: OFFLINE_BACKDROP_Z_INDEX,
        }}
      />

      {/* Foreground modal content */}
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: OFFLINE_MESSAGE_Z_INDEX,
          pointerEvents: 'auto',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: 'min(900px, 90vw)',
            minHeight: '40vh',
            borderRadius: '8px',
            background: 'linear-gradient(229.67deg, #1D2F44 -6.17%, #071423 89.04%)',
            overflow: 'hidden',
            px: { xs: 3, md: 6 },
            py: { xs: 6, md: 8 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {/* Rotating broken/keyboard image for visual consistency with 404 */}
          {!pictureFailed && (
            <Box sx={{ position: 'absolute', animation: 'error-page-rotation 6s infinite linear' }}>
              <img
                style={{ maxWidth: '1000px', width: '75vw', opacity: 0.3 }}
                src={pictureSrc}
                alt=""
                onError={() => setPictureFailed(true)}
              />
            </Box>
          )}

          <Box sx={{ position: 'relative', textAlign: 'center' }}>
            <Box
              sx={{
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: { xs: '28px', md: '34px' },
                letterSpacing: '0.01em',
                color: '#CED0D4',
                mb: 2,
              }}
            >
              You are offline
            </Box>
            <Box
              sx={{
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: { xs: '16px', md: '20px' },
                lineHeight: '30px',
                letterSpacing: '0.01em',
                color: '#CED0D4',
                maxWidth: '700px',
                mx: 'auto',
              }}
            >
              Please check your internet connection. This screen will close automatically once you
              are back online.
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default OfflineModal;
